import React, { useState, useEffect } from 'react';
import { X, Building2, Users, Tag, FileText } from 'lucide-react';
import { Team } from '../../types';
import { teamAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team?: Team | null;
  onTeamUpdated: () => void;
}

const TeamModal: React.FC<TeamModalProps> = ({
  isOpen,
  onClose,
  team,
  onTeamUpdated
}) => {
  const [formData, setFormData] = useState({
    institution: '',
    lab_name: '',
    team_name: '',
    description: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (team) {
        setFormData({
          institution: team.institution || '',
          lab_name: team.lab_name || '',
          team_name: team.team_name || '',
          description: team.description || ''
        });
      } else {
        setFormData({
          institution: '',
          lab_name: '',
          team_name: '',
          description: ''
        });
      }
    }
  }, [isOpen, team]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.institution.trim() || !formData.lab_name.trim() || !formData.team_name.trim()) {
      toast.error('Institution, Lab Name, and Team Name are required');
      return;
    }

    setIsLoading(true);
    try {
      if (team) {
        // Update existing team
        await teamAPI.updateTeam(team.id, formData);
        toast.success('Team updated successfully');
      } else {
        // Create new team
        await teamAPI.createTeam(formData);
        toast.success('Team created successfully');
      }
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern('/admin/batch');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/teams/batch');
      
      onTeamUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error saving team:', error);
      toast.error(error.response?.data?.error || 'Failed to save team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {team ? 'Edit Team' : 'Add New Team'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {team ? 'Update team information' : 'Create a new research team'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Institution */}
          <div>
            <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Institution *
            </label>
            <input
              type="text"
              id="institution"
              name="institution"
              value={formData.institution}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., UCSF"
              required
            />
          </div>

          {/* Lab Name */}
          <div>
            <label htmlFor="lab_name" className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="inline h-4 w-4 mr-1" />
              Lab Name *
            </label>
            <input
              type="text"
              id="lab_name"
              name="lab_name"
              value={formData.lab_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Savic Lab"
              required
            />
          </div>

          {/* Team Name */}
          <div>
            <label htmlFor="team_name" className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="inline h-4 w-4 mr-1" />
              Team Name *
            </label>
            <input
              type="text"
              id="team_name"
              name="team_name"
              value={formData.team_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Translational TB"
              required
            />
          </div>

          {/* Full Name Preview */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Team Name (Preview)
            </label>
            <div className="text-lg font-medium text-gray-900">
              {formData.institution && formData.lab_name && formData.team_name
                ? `${formData.institution} ${formData.lab_name} ${formData.team_name}`
                : 'Enter institution, lab name, and team name to see preview'
              }
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional description of the team's focus and research area"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : (team ? 'Update Team' : 'Create Team')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamModal;
